<template>
  <section class="card">
    <header class="head">
      <h2>服务登记管理</h2>
      <p class="muted">走访 / 来访 / 会员服务及自定义服务表 · 登记记录、表单字段与统计 Excel 模板分开维护</p>
    </header>

    <div class="sub-tabs">
      <button
        v-for="t in subTabs"
        :key="t.key"
        type="button"
        :class="['sub-tab', activeTab === t.key && 'sub-tab--on']"
        @click="switchTab(t.key)"
      >{{ t.label }}</button>
    </div>

    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <template v-if="activeTab === 'records'">
    <section class="report-panel">
      <div class="report-head">
        <h3>统计报表</h3>
        <div class="report-periods">
          <button
            v-for="p in reportPeriods"
            :key="p.value"
            type="button"
            :class="['report-period-btn report-action-btn', reportPeriod === p.value && 'on']"
            @click="setReportPeriod(p.value)"
          >{{ p.label }}</button>
        </div>
      </div>
      <div class="report-controls">
        <button type="button" class="btn btn-edit report-action-btn" @click="shiftReportAnchor(-1)">上一{{ reportPeriodLabel }}</button>
        <input v-model="reportAnchor" class="flt-input flt-input--date report-date-input" type="date" @change="onReportDateChange" />
        <button type="button" class="btn btn-edit report-action-btn" @click="shiftReportAnchor(1)">下一{{ reportPeriodLabel }}</button>
        <button type="button" class="btn btn-primary report-action-btn" :disabled="reportLoading" @click="loadReports">刷新统计</button>
        <button type="button" class="btn btn-edit report-action-btn" :disabled="reportExportBusy" @click="exportReport">导出 Excel</button>
        <button type="button" class="btn btn-edit report-action-btn" @click="applyReportListFilter">筛选下方列表</button>
      </div>
      <p v-if="reportSummary.rangeLabel" class="muted report-range">
        {{ reportSummary.rangeLabel }} · 共 {{ reportSummary.totalCount || 0 }} 条
        <span class="report-range-hint">（按提交时间统计）</span>
        <button
          v-if="hasReportBreakdown"
          type="button"
          class="link-btn report-toggle-btn"
          @click="reportSummaryOpen = !reportSummaryOpen"
        >{{ reportSummaryOpen ? '收起汇总' : '查看汇总' }}</button>
      </p>
      <p v-if="reportLoading" class="muted">统计加载中…</p>
      <div v-else-if="reportSummaryOpen && hasReportBreakdown" class="report-stats report-stats--compact">
        <div v-if="reportTypeStats.length" class="report-stat-block report-stat-block--inline">
          <strong>类型</strong>
          <span v-for="(it, idx) in reportTypeStats" :key="'t-' + idx" class="report-chip">{{ it.label }} {{ it.count }}</span>
        </div>
        <div v-if="reportOwnerStats.length" class="report-stat-block report-stat-block--inline">
          <strong>负责人</strong>
          <span v-for="(it, idx) in reportOwnerStats" :key="'o-' + idx" class="report-chip report-chip--muted">{{ it.label }} {{ it.count }}</span>
        </div>
      </div>
    </section>

    <div class="filters filters-panel">
      <div class="flt">
        <FancySelect
          v-model="formCode"
          class="flt-select"
          :options="formOptions"
          placeholder="表单类型"
        />
      </div>
      <div class="flt">
        <input
          v-model.trim="keyword"
          class="flt-input flt-input--wide"
          placeholder="单位 / 联系人 / 手机 / 登记人"
          aria-label="单位、联系人、手机、登记人"
          @keyup.enter="search"
        />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" :disabled="loading" @click="search">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <div class="table-toolbar">
      <span class="muted">共 {{ rows.length }} 条</span>
      <span v-if="listSyncReport" class="list-filter-tag">
        已按统计周期筛选
        <button type="button" class="link-btn" @click="showAllList">显示全部</button>
      </span>
    </div>

    <div class="table-wrap">
      <table class="data-table">
        <thead>
          <tr>
            <th>序号</th>
            <th>表单类型</th>
            <th>单位</th>
            <th>联系人</th>
            <th>手机</th>
            <th>登记人</th>
            <th>服务时间</th>
            <th>提交时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in pagedRows"
            :key="row.id"
            :class="{ 'row-focus': String(focusId) === String(row.id) }"
          >
            <td>{{ rowOffset + index + 1 }}</td>
            <td>{{ row.formName || '—' }}</td>
            <td><span class="ellipsis" :title="row.summaryUnit">{{ row.summaryUnit || '—' }}</span></td>
            <td>{{ row.summaryName || '—' }}</td>
            <td>{{ row.summaryPhone || '—' }}</td>
            <td>{{ row.submitterName || '—' }}</td>
            <td>{{ row.serviceTime || '—' }}</td>
            <td>{{ formatTime(row.createdAt) }}</td>
            <td>
              <button class="btn btn-edit" type="button" @click="openEdit(row)">编辑</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="rows.length"
    />
    <p v-if="!loading && !rows.length" class="muted empty-hint">暂无登记记录</p>

    <ServiceFormEditDialog
      v-model:visible="editVisible"
      :submission-id="editId"
      @saved="onSaved"
    />
    </template>

    <section v-else-if="activeTab === 'config'" class="config-panel">
      <div class="config-toolbar">
        <p class="muted config-tip">
          配置小程序「服务表单」填写项（字段、分组、类型）。与下方「Excel 模板」独立：改字段不会自动改导出列。
        </p>
        <button type="button" class="btn btn-primary" @click="openCreateForm">添加表单</button>
      </div>
      <div class="config-grid">
        <div v-for="item in configForms" :key="item.id || item.code" class="config-card">
          <button type="button" class="config-edit" title="修改名称" @click="openRenameForm(item)">
            <img class="config-edit-icon" :src="treeNavEditIcon" alt="修改名称" />
          </button>
          <h3>{{ item.name }}</h3>
          <p class="muted">{{ item.description || '服务登记表' }}</p>
          <code class="config-code">{{ item.code }}</code>
          <button type="button" class="btn btn-primary" @click="goFormConfig(item)">维护字段</button>
        </div>
      </div>
      <p v-if="!configForms.length" class="muted empty-hint">暂无服务表单，请点击「添加表单」</p>
    </section>

    <section v-else-if="activeTab === 'excel'" class="excel-panel">
      <p class="muted excel-tip">
        统计导出共用一份 Excel 模板：第 <strong>1</strong> 行表头（列名与样式）、第 <strong>2</strong> 行各列
        <code>fieldKey</code>（系统项如 <code>serviceType</code>、<code>formName</code>，或表单字段 key）。
        第 <strong>3</strong> 行起由系统填数据；某表单没有该字段则留空。
      </p>
      <div class="excel-status">
        <span>模板状态：</span>
        <strong>{{ excelTemplateInfo.uploaded ? '已上传' : '未上传（将使用系统默认五列）' }}</strong>
        <span v-if="excelTemplateInfo.updatedAt" class="muted"> · 更新于 {{ excelTemplateInfo.updatedAt }}</span>
      </div>
      <div class="excel-actions">
        <label class="btn btn-primary excel-file">
          上传 .xlsx 模板
          <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" class="excel-file-input" @change="onExcelTemplateFile" />
        </label>
        <button type="button" class="btn btn-edit" :disabled="!excelTemplateInfo.uploaded" @click="downloadExcelTemplate">下载当前模板</button>
        <button type="button" class="btn btn-delete" :disabled="!excelTemplateInfo.uploaded || excelUploadBusy" @click="clearExcelTemplate">清除模板</button>
      </div>
      <p v-if="excelMsg" :class="excelMsgOk ? 'ok' : 'error'">{{ excelMsg }}</p>
    </section>

    <div v-if="showFormMetaDialog" class="mask" @click.self="closeFormMetaDialog">
      <div class="dialog dialog--shell dialog--sm" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ formMetaMode === 'create' ? '添加表单' : '修改表单名称' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeFormMetaDialog">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="formMetaError" class="error">{{ formMetaError }}</p>
          <label class="meta-field">
            <span>表单名称<span class="req">*</span></span>
            <input
              v-model.trim="formMetaForm.name"
              class="meta-input"
              maxlength="100"
              placeholder="请输入表单名称"
              @keyup.enter="saveFormMeta"
            />
          </label>
          <label class="meta-field">
            <span>表单说明</span>
            <input
              v-model.trim="formMetaForm.description"
              class="meta-input"
              maxlength="200"
              placeholder="可留空"
            />
          </label>
          <p v-if="formMetaMode === 'create'" class="muted meta-hint">编码将由系统自动生成，创建后可维护字段。</p>
          <p v-else class="muted meta-hint">编码：{{ formMetaForm.code || '—' }}（不可修改）</p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="formMetaSaving" @click="closeFormMetaDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="formMetaSaving" @click="saveFormMeta">
            {{ formMetaSaving ? '保存中…' : (formMetaMode === 'create' ? '创建' : '保存') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import ServiceFormEditDialog from '@/components/ServiceFormEditDialog.vue'
import treeNavEditIcon from '@/assets/icons/tree-nav-edit.svg'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import {
  clearServiceFormReportTemplate,
  createForm,
  downloadServiceFormReport,
  downloadServiceFormReportTemplate,
  getForms,
  getServiceFormCatalog,
  getServiceFormReportSummary,
  getServiceFormReportTemplateInfo,
  getServiceFormSubmissions,
  updateForm,
  uploadServiceFormReportTemplate,
} from '@/api'

function todayYmd() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export default {
  name: 'ServiceFormManageView',
  components: { FancySelect, ListPager, ServiceFormEditDialog },
  data() {
    return {
      treeNavEditIcon,
      subTabs: [
        { key: 'records', label: '登记记录' },
        { key: 'config', label: '表单配置' },
        { key: 'excel', label: 'Excel 模板' },
      ],
      activeTab: 'records',
      configForms: [],
      excelTemplateInfo: { uploaded: false },
      excelUploadBusy: false,
      excelMsg: '',
      excelMsgOk: false,
      loading: false,
      errorMsg: '',
      catalog: [],
      formCode: '',
      keyword: '',
      page: 1,
      pageSize: 10,
      rows: [],
      focusId: '',
      editVisible: false,
      editId: 0,
      reportPeriods: [
        { value: 'day', label: '日报' },
        { value: 'week', label: '周报' },
        { value: 'year', label: '年报' },
      ],
      reportPeriod: 'day',
      reportAnchor: todayYmd(),
      reportLoading: false,
      reportExportBusy: false,
      reportSummary: {},
      reportSummaryOpen: false,
      listSyncReport: false,
      showFormMetaDialog: false,
      formMetaMode: 'create',
      formMetaSaving: false,
      formMetaError: '',
      formMetaForm: {
        id: null,
        code: '',
        name: '',
        description: '',
        statusCode: 'ENABLED',
        version: 1,
        remark: '',
      },
    }
  },
  computed: {
    hasReportBreakdown() {
      return this.reportTypeStats.length > 0 || this.reportOwnerStats.length > 0
    },
    formOptions() {
      const all = [{ value: '', label: '全部' }]
      const list = Array.isArray(this.catalog) ? this.catalog : []
      return all.concat(list.map((f) => ({ value: f.code, label: f.name || f.code })))
    },
    reportTypeStats() {
      return Array.isArray(this.reportSummary.byServiceType) ? this.reportSummary.byServiceType : []
    },
    reportOwnerStats() {
      return Array.isArray(this.reportSummary.bySecretariatOwner) ? this.reportSummary.bySecretariatOwner : []
    },
    reportPeriodLabel() {
      if (this.reportPeriod === 'week') return '周'
      if (this.reportPeriod === 'year') return '年'
      return '日'
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedRows() {
      return slicePageRows(this.rows, this.page, this.pageSize)
    },
  },
  watch: {
    'rows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    this.syncTabFromRoute()
    this.focusId = String((this.$route && this.$route.query && this.$route.query.focusId) || '').trim()
    this.loadCatalog()
    if (this.activeTab === 'records') {
      this.loadReports()
      this.load().then(() => this.tryOpenFocus())
    } else if (this.activeTab === 'config') {
      this.loadConfigForms()
    } else if (this.activeTab === 'excel') {
      this.loadExcelTemplateInfo()
    }
  },
  methods: {
    syncTabFromRoute() {
      const tab = String((this.$route && this.$route.query && this.$route.query.tab) || '').trim()
      if (tab === 'config' || tab === 'excel' || tab === 'records') {
        this.activeTab = tab
      }
    },
    switchTab(key) {
      this.activeTab = key
      this.page = 1
      this.errorMsg = ''
      this.excelMsg = ''
      const q = { ...(this.$route.query || {}), tab: key }
      this.$router.replace({ path: this.$route.path, query: q }).catch(() => {})
      if (key === 'records') {
        this.loadReports()
        this.load()
      } else if (key === 'config') {
        this.loadConfigForms()
      } else if (key === 'excel') {
        this.loadExcelTemplateInfo()
      }
    },
    goFormConfig(item) {
      const code = typeof item === 'string' ? item : String((item && item.code) || '').trim()
      if (!code) return
      const name = typeof item === 'object' && item ? String(item.name || '') : ''
      const description = typeof item === 'object' && item ? String(item.description || '') : ''
      this.$router.push({
        path: `/member/service-form-config/${encodeURIComponent(code)}`,
        query: { name, description },
      })
    },
    async loadConfigForms() {
      try {
        const list = await getForms({ category: 'SERVICE' })
        this.configForms = (Array.isArray(list) ? list : [])
          .filter((f) => String(f.statusCode || 'ENABLED').toUpperCase() !== 'DISABLED')
          .slice()
          .sort((a, b) => Number(a.id || 0) - Number(b.id || 0))
        if (!this.configForms.length) {
          const catalog = await getServiceFormCatalog()
          this.configForms = Array.isArray(catalog) ? catalog : []
        }
      } catch {
        try {
          const catalog = await getServiceFormCatalog()
          this.configForms = Array.isArray(catalog) ? catalog : []
        } catch {
          this.configForms = []
        }
      }
    },
    openCreateForm() {
      this.formMetaMode = 'create'
      this.formMetaError = ''
      this.formMetaForm = {
        id: null,
        code: '',
        name: '',
        description: '',
        statusCode: 'ENABLED',
        version: 1,
        remark: '',
      }
      this.showFormMetaDialog = true
    },
    openRenameForm(item) {
      if (!item || item.id == null) {
        window.alert('缺少表单 id，请刷新后重试')
        return
      }
      this.formMetaMode = 'rename'
      this.formMetaError = ''
      this.formMetaForm = {
        id: item.id,
        code: item.code || '',
        name: item.name || '',
        description: item.description || '',
        statusCode: item.statusCode || 'ENABLED',
        version: item.version == null ? 1 : item.version,
        remark: item.remark || '',
      }
      this.showFormMetaDialog = true
    },
    closeFormMetaDialog() {
      if (this.formMetaSaving) return
      this.showFormMetaDialog = false
      this.formMetaError = ''
    },
    async saveFormMeta() {
      const name = String(this.formMetaForm.name || '').trim()
      if (!name) {
        this.formMetaError = '表单名称不能为空'
        return
      }
      this.formMetaSaving = true
      this.formMetaError = ''
      try {
        if (this.formMetaMode === 'create') {
          const created = await createForm({
            name,
            description: String(this.formMetaForm.description || '').trim(),
            statusCode: 'ENABLED',
            version: 1,
            formCategory: 'SERVICE',
          })
          await this.loadConfigForms()
          await this.loadCatalog()
          this.showFormMetaDialog = false
          if (created && created.code) {
            this.goFormConfig(created)
          }
        } else {
          await updateForm(this.formMetaForm.id, {
            code: this.formMetaForm.code,
            name,
            description: String(this.formMetaForm.description || '').trim(),
            statusCode: this.formMetaForm.statusCode || 'ENABLED',
            version: this.formMetaForm.version == null ? 1 : this.formMetaForm.version,
            remark: this.formMetaForm.remark || '',
            formCategory: 'SERVICE',
          })
          await this.loadConfigForms()
          await this.loadCatalog()
          this.showFormMetaDialog = false
        }
      } catch (e) {
        this.formMetaError = (e && e.message) || (this.formMetaMode === 'create' ? '创建失败' : '修改失败')
      } finally {
        this.formMetaSaving = false
      }
    },
    async loadExcelTemplateInfo() {
      try {
        this.excelTemplateInfo = (await getServiceFormReportTemplateInfo()) || { uploaded: false }
      } catch {
        this.excelTemplateInfo = { uploaded: false }
      }
    },
    async onExcelTemplateFile(e) {
      const input = e.target
      const file = input.files && input.files[0]
      if (!file) return
      this.excelUploadBusy = true
      this.excelMsg = ''
      try {
        this.excelTemplateInfo = (await uploadServiceFormReportTemplate(file)) || { uploaded: true }
        this.excelMsg = '模板已上传'
        this.excelMsgOk = true
      } catch (err) {
        this.excelMsg = (err && err.message) || '上传失败'
        this.excelMsgOk = false
      } finally {
        this.excelUploadBusy = false
        input.value = ''
      }
    },
    async downloadExcelTemplate() {
      try {
        await downloadServiceFormReportTemplate()
      } catch (err) {
        window.alert((err && err.message) || '下载失败')
      }
    },
    async clearExcelTemplate() {
      if (!await adminConfirm('确定清除已上传的 Excel 模板？清除后将恢复默认五列导出。')) return
      this.excelUploadBusy = true
      try {
        this.excelTemplateInfo = (await clearServiceFormReportTemplate()) || { uploaded: false }
        this.excelMsg = '已清除模板'
        this.excelMsgOk = true
      } catch (err) {
        this.excelMsg = (err && err.message) || '清除失败'
        this.excelMsgOk = false
      } finally {
        this.excelUploadBusy = false
      }
    },
    formatTime(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.length > 19 ? s.slice(0, 19) : s
    },
    async loadCatalog() {
      try {
        const list = await getServiceFormCatalog()
        this.catalog = Array.isArray(list) ? list : []
      } catch {
        this.catalog = []
      }
    },
    resetFilters() {
      this.page = 1
      this.formCode = ''
      this.keyword = ''
      this.listSyncReport = false
      this.load()
    },
    listQueryParams() {
      const params = {}
      if (this.listSyncReport) {
        params.period = this.reportPeriod
        params.date = this.reportAnchor
      }
      if (this.formCode) params.code = this.formCode
      if (this.keyword) params.q = this.keyword
      return params
    },
    search() {
      this.page = 1
      this.load()
    },
    async load() {
      this.loading = true
      this.errorMsg = ''
      try {
        const list = await getServiceFormSubmissions(this.listQueryParams())
        this.rows = Array.isArray(list) ? list : []
      } catch (e) {
        this.errorMsg = (e && e.message) || '加载失败'
        this.rows = []
      } finally {
        this.loading = false
      }
    },
    openEdit(row) {
      const id = Number((row && row.id) || 0)
      if (!id) return
      this.editId = id
      this.editVisible = true
    },
    tryOpenFocus() {
      const id = Number(this.focusId || 0)
      if (!id) return
      const row = (this.rows || []).find((r) => Number(r.id) === id)
      if (row) this.openEdit(row)
    },
    async onSaved() {
      await this.load()
      await this.loadReports()
      window.alert('已保存修改')
    },
    setReportPeriod(v) {
      this.reportPeriod = v
      this.reportSummaryOpen = false
      this.onReportControlsChange()
    },
    shiftReportAnchor(dir) {
      const d = Number(dir || 0)
      const p = String(this.reportAnchor || todayYmd()).split('-').map(Number)
      const dt = new Date(p[0], (p[1] || 1) - 1, p[2] || 1)
      if (this.reportPeriod === 'year') dt.setFullYear(dt.getFullYear() + d)
      else if (this.reportPeriod === 'week') dt.setDate(dt.getDate() + 7 * d)
      else dt.setDate(dt.getDate() + d)
      const y = dt.getFullYear()
      const m = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      this.reportAnchor = `${y}-${m}-${day}`
      this.onReportControlsChange()
    },
    onReportDateChange() {
      this.onReportControlsChange()
    },
    onReportControlsChange() {
      this.loadReports()
      if (this.listSyncReport) this.load()
    },
    applyReportListFilter() {
      this.listSyncReport = true
      this.loadReports()
      this.load()
    },
    showAllList() {
      this.listSyncReport = false
      this.page = 1
      this.load()
    },
    reportQuery() {
      const q = { period: this.reportPeriod, date: this.reportAnchor }
      if (this.formCode) q.code = this.formCode
      return q
    },
    async loadReports() {
      this.reportLoading = true
      try {
        this.reportSummary = await getServiceFormReportSummary(this.reportQuery())
      } catch {
        this.reportSummary = {}
      } finally {
        this.reportLoading = false
      }
    },
    async exportReport() {
      this.reportExportBusy = true
      try {
        await downloadServiceFormReport(this.reportQuery())
      } catch (e) {
        window.alert((e && e.message) || '导出失败')
      } finally {
        this.reportExportBusy = false
      }
    },
  },
}
</script>

<style scoped>
.card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 22px 24px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);
}
.head h2 { margin: 0 0 8px; font-size: 20px; }
.sub-tabs {
  display: flex;
  gap: 8px;
  margin: 16px 0 20px;
  flex-wrap: wrap;
}
.sub-tab {
  border: 1px solid #d0d5dd;
  background: #fff;
  border-radius: 999px;
  padding: 8px 18px;
  font-size: 14px;
  cursor: pointer;
  color: #344054;
}
.sub-tab--on {
  background: #2f6eff;
  border-color: #2f6eff;
  color: #fff;
}
.config-panel,
.excel-panel {
  padding-top: 4px;
}
.config-tip,
.excel-tip {
  margin: 0 0 16px;
  line-height: 1.6;
}
.config-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}
.config-toolbar .config-tip {
  margin: 0;
  flex: 1;
}
.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 16px;
}
.config-card {
  position: relative;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  padding: 16px 18px;
  background: #fafbfc;
}
.config-edit {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.config-edit:hover {
  border-color: #2f6eff;
}
.config-edit-icon {
  width: 16px;
  height: 16px;
  display: block;
}
.config-card h3 { margin: 0 28px 8px 0; font-size: 17px; }
.meta-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
  font-size: 13px;
  color: #344054;
}
.meta-field > span { font-weight: 600; }
.meta-input {
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  background: #fff;
}
.meta-input:focus {
  outline: none;
  border-color: #2f6af6;
  box-shadow: 0 0 0 3px rgba(47, 106, 246, 0.12);
}
.meta-hint { margin: 0; line-height: 1.5; }
.req { color: #d92d20; margin-left: 2px; }
.config-code {
  display: block;
  margin: 10px 0 14px;
  font-size: 12px;
  color: #667085;
}
.excel-status { margin-bottom: 14px; font-size: 14px; }
.excel-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}
.excel-file {
  position: relative;
  cursor: pointer;
}
.excel-file-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.ok { color: #039855; margin-top: 12px; }
.muted { color: #667085; font-size: 13px; }
.error { color: #d92d20; margin: 12px 0 0; }
.filters .flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  font-size: 13px;
  background: #fff;
}
.filters .flt-input--wide {
  width: 280px;
}
.filters .flt-select {
  width: 170px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.table-toolbar {
  margin: 16px 0 8px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}
.list-filter-tag {
  font-size: 13px;
  color: #667085;
}
.link-btn {
  border: none;
  background: none;
  padding: 0 0 0 6px;
  color: #2f6af6;
  cursor: pointer;
  font-size: 13px;
  text-decoration: underline;
}
.table-wrap { overflow: auto; }
.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.data-table th,
.data-table td {
  border-bottom: 1px solid #eaecf0;
  padding: 10px 12px;
  text-align: center;
  vertical-align: middle;
}
.data-table th { background: #f9fafb; color: #475467; font-weight: 600; }
.row-focus {
  background: #fffaeb;
}
.ellipsis {
  display: inline-block;
  max-width: 220px;
  margin: 0 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
.empty-hint { margin-top: 16px; text-align: center; }
.report-panel {
  margin: 16px 0 20px;
  padding: 16px 18px;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  background: #fafbfc;
}
.report-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.report-head h3 { margin: 0; font-size: 16px; }
.report-periods { display: flex; gap: 8px; }
.report-action-btn {
  min-width: 108px;
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}
.report-period-btn {
  border: 1px solid #d0d7e2;
  background: #fff;
  color: #344054;
  cursor: pointer;
}
.report-period-btn.on {
  border-color: #2f6af6;
  background: #eff4ff;
  color: #2f6af6;
}
.report-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.report-controls .report-date-input {
  width: 160px;
  min-width: 160px;
  height: 40px;
  box-sizing: border-box;
}
.report-controls .btn + .btn {
  margin-left: 0;
}
.report-range { margin: 0 0 10px; }
.report-range-hint { color: #98a2b3; font-size: 12px; }
.report-toggle-btn { margin-left: 8px; }
.report-stats--compact {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}
.report-stat-block--inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}
.report-stat-block--inline strong {
  color: #475467;
  font-weight: 600;
  margin-right: 4px;
}
.report-chip {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  background: #eef4ff;
  color: #2f6af6;
  font-size: 12px;
}
.report-chip--muted {
  background: #f2f4f7;
  color: #667085;
}
.report-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}
.report-stat-block ul {
  list-style: none;
  margin: 8px 0 0;
  padding: 0;
}
.report-stat-block li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-bottom: 1px solid #eef2f7;
  font-size: 13px;
}
.report-stat-block li em {
  font-style: normal;
  font-weight: 600;
  color: #2f6af6;
}
.btn {
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 14px;
  box-sizing: border-box;
}
.report-panel .btn {
  min-width: 108px;
  height: 40px;
  padding: 0 16px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { background: #eef4ff; color: #2f6af6; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
</style>
